import React from 'react'
import PropTypes from 'prop-types'
import { ScrollArea, sendHoverSoundCommand, sendSelectClickSoundCommand } from 'react-wot-bootstrap'
import classNames from 'classnames'
import { connect } from 'react-redux'

import Clan from 'components/Clan/Clan'
import { UNIT_TYPES } from 'constants'
import { UnitTooltip } from 'containers/BattleRooms/components'
import { selectUnit } from 'state/actions/available_units'
import { joinToUnit } from 'state/actions/join_unit'

import { isUnitDisabled } from '../../helpers'
import {
    UNITS_LIST_ITEM_HEIGHT,
    UNITS_LIST_OTHERS_CLANS_SUBTITLE,
    UNITS_LIST_VISIBLE_ITEMS,
} from './constants'
import { UnitDataPropTypes } from 'containers/BattleRooms/propTypes'
import EmptyList from '../EmptyList/EmptyList'

import styles from '../../UnitsList.scss'

export class TableBody extends React.Component {
    static propTypes = {
        filter: PropTypes.shape({
            level: PropTypes.number,
            unitType: PropTypes.oneOf(Object.values(UNIT_TYPES)),
        }).isRequired,
        updateCooldown: PropTypes.bool.isRequired,
        updateListHandler: PropTypes.func.isRequired,

        clanId: PropTypes.number,
        loading: PropTypes.bool,
        units: PropTypes.arrayOf(UnitDataPropTypes).isRequired,
        availablePeripheries: PropTypes.array.isRequired,
        selectedUnitId: PropTypes.number,

        joinToUnit: PropTypes.func.isRequired,
        selectUnit: PropTypes.func.isRequired,
    }

    constructor() {
        super()
        this.state = { hoveredUnitId: null }
        this.handleRowClick = this.handleRowClick.bind(this)
        this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this)
        this.hoverUnitHandler = this.hoverUnitHandler.bind(this)
    }

    componentDidUpdate(prevProps) {
        if (this._scrollArea && this._scrollArea.state.topArrowEnabled) {  // in tests there is no _scrollArea
            const wasFilterChanged = JSON.stringify(prevProps.filter) !== JSON.stringify(this.props.filter)
            if (wasFilterChanged) this._scrollArea.scrollToTop()
        }
    }

    handleRowClick(item) {
        if (item.id !== this.props.selectedUnitId) {
            this.props.selectUnit(item)
            sendSelectClickSoundCommand()
        }
    }

    handleRowDoubleClick() {
        const selectedUnit = this.props.units.find(unit => unit.id === this.props.selectedUnitId)
        this.props.joinToUnit(selectedUnit.server_id, selectedUnit.periphery.id)
    }

    hoverUnitHandler(unitId) {
        if (unitId) sendHoverSoundCommand()
        this.setState({ hoveredUnitId: unitId })
    }

    renderSubtitleRow() {
        const subtitleRowClassName = classNames(styles.tr, styles.tr__subtitle)
        return (
            <div className={subtitleRowClassName} key={'available_units_row_subtitle'}>
                <div className={styles.td}>{UNITS_LIST_OTHERS_CLANS_SUBTITLE}</div>
            </div>
        )
    }

    renderRow(unit, idx) {
        const {
            average_rating,
            clan,
            max_members_number,
            members_number,
            periphery,
        } = unit

        const freePlaces = (
            (this.props.clanId === clan.id) ? unit.free_places_for_clan_members : unit.free_places_for_legionaries
        )

        const basisTDClassName = classNames(styles.td, styles.td__basis)
        const centeredTDClassName = classNames(styles.td, styles.td__center)
        const trClassName = classNames(styles.tr, {
            [styles.tr__selected]: unit.id === this.props.selectedUnitId,
        })

        const trProps = {
            className: trClassName,
            key: `available_units_row_${idx}`,
            onClick: () => this.handleRowClick(unit),
            onDoubleClick: () => this.handleRowDoubleClick(),
            onMouseEnter: () => this.hoverUnitHandler(unit.id),
            onMouseLeave: () => this.hoverUnitHandler(null),
            'data-tip': true,
            'data-for': UnitTooltip.id,
        }

        if (isUnitDisabled(unit, this.props.clanId)) {
            const mdash = <span>{'\u2014'}</span>
            const emptyTd = (
                <div className={centeredTDClassName}>
                    {mdash}
                </div>
            )
            return (
                <div {...trProps}>
                    <div className={basisTDClassName}>
                        <Clan {...clan} />
                    </div>
                    <div className={centeredTDClassName}>
                        <span className={styles.rating}>{mdash}</span>
                    </div>
                    {emptyTd}
                    {emptyTd}
                    {emptyTd}
                </div>
            )
        }

        const inBattleIcon = (unit.in_battle || unit.players_matching_started_at !== null) ? (
            <span className={styles.inBattleIcon} />
        ) : null

        return (
            <div {...trProps}>
                <div className={basisTDClassName}>
                    <Clan {...clan} />
                </div>
                <div className={centeredTDClassName}>
                    <span className={styles.rating}>{average_rating}</span>
                </div>
                <div className={centeredTDClassName}>{periphery.name}</div>
                <div className={centeredTDClassName}>
                    {members_number}
                    <span className={styles.totalCount}>{`/${max_members_number}`}</span>
                </div>
                <div className={centeredTDClassName}>{freePlaces}</div>
                {inBattleIcon}
            </div>
        )
    }


    render() {
        const { availablePeripheries, units, loading, clanId } = this.props
        if (!units.length && !loading) {
            return (
                <EmptyList
                    handleListUpdate={this.props.updateListHandler}
                    updateCooldown={this.props.updateCooldown}
                />
            )
        }

        const rows = []
        const availableUnits = units.filter(item => availablePeripheries.includes(item.periphery.id))
        const myClanUnits = availableUnits.filter(item => item.clan.id === clanId)
        const otherClansUnits = availableUnits.filter(item => item.clan.id !== clanId)

        if (myClanUnits.length) {
            rows.push(myClanUnits.map((item, idx) => this.renderRow(item, idx)))
        }
        if (myClanUnits.length && otherClansUnits.length) {
            rows.push(this.renderSubtitleRow())
        }
        rows.push(otherClansUnits.map((item, idx) => this.renderRow(item, idx + myClanUnits.length)))

        let unitTooltipComponent
        if (this.state.hoveredUnitId) {
            unitTooltipComponent = <UnitTooltip unitId={this.state.hoveredUnitId} />
        }
        return (
            <div>
                <ScrollArea
                    stepHeight={UNITS_LIST_ITEM_HEIGHT}
                    scrollableAreaHeight={UNITS_LIST_ITEM_HEIGHT * UNITS_LIST_VISIBLE_ITEMS}
                    modifier='unitsList'
                    ref={elem => this._scrollArea = elem}
                >
                    <div className={styles.table}>
                        <div className={styles.tbody}>
                            {rows}
                        </div>
                    </div>
                </ScrollArea>
                {unitTooltipComponent}
            </div>
        )
    }
}

export const mapStateToProps = state => {
    const availablePeripheries = [...state.availablePeripheries.ids, state.account.periphery_id]

    return {
        clanId: state.clan.id,
        loading: state.availableUnits.loading,
        units: state.availableUnits.units,
        availablePeripheries,
        selectedUnitId: state.availableUnits.selectedUnitId,
    }
}

const mapDispatchToProps = {
    joinToUnit,
    selectUnit,
}

export default connect(mapStateToProps, mapDispatchToProps)(TableBody)



// WEBPACK FOOTER //
// ./containers/BattleRooms/components/UnitsList/components/TableBody/TableBody.jsx